import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiFetch, getBackendUrl, getUserFacingErrorMessage, throwIfNotOk } from './services/apiClient';
import './ScheduleInterviewPage.css';

const ScheduleInterviewPage = () => {
    const { jobId, resumeId, token } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Token-only URL: /select-slots/t/:token (no job/resume ids in URL)
    const isTokenRoute = Boolean(token);
    // Select Slots mode: candidate opens link from email (token or legacy jobId/resumeId)
    const isSelectSlotsMode = isTokenRoute || location.pathname.includes('/select-slots/');
    const [job, setJob] = useState(null);
    const [resume, setResume] = useState(null);
    const [interview, setInterview] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isScheduling, setIsScheduling] = useState(false);
    const [purpose, setPurpose] = useState('interview');
    const [interviewTitle, setInterviewTitle] = useState('');
    const isRecruiterScreening = purpose === 'recruiter_screening';
    const meetingLabel = isRecruiterScreening ? 'recruiter screening' : 'interview';
    const durationMinutes = interview?.duration_minutes || (isRecruiterScreening ? 30 : 60);

    useEffect(() => {
        if (isTokenRoute) {
            loadSlotPageByToken();
        } else {
            fetchJobAndResume();
            fetchInterviewData();
            fetchAvailableSlots();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, resumeId, token, isTokenRoute]);

    const loadSlotPageByToken = async () => {
        if (!token) return;
        try {
            setIsLoading(true);
            setError(null);
            const url = `${getBackendUrl()}/api/public/slot-page-info/by-token/${encodeURIComponent(token)}`;
            const res = await fetch(url);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.detail || 'Invalid or expired scheduling link. Please request a new one.');
                setJob(null);
                setResume(null);
                setAvailableSlots([]);
                return;
            }
            setJob(data.job || null);
            setResume(data.resume || null);
            setAvailableSlots(data.available_slots || []);
            setPurpose(data.purpose || data.display_kind || 'interview');
            setInterviewTitle(data.interview_title || '');
            setInterview({
                duration_minutes:
                    data.duration_minutes ||
                    (data.purpose === 'recruiter_screening' ? 30 : 60),
            });
        } catch (err) {
            setError(err.message || 'Failed to load scheduling page');
            setJob(null);
            setResume(null);
            setAvailableSlots([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchJobAndResume = async () => {
        try {
            if (isSelectSlotsMode && !isTokenRoute) {
                // Public slot page (legacy jobId/resumeId URL): unauthenticated endpoint
                const publicResponse = await apiFetch(`/api/public/slot-page-info/${jobId}/${resumeId}`);
                if (publicResponse.ok) {
                    const data = await publicResponse.json();
                    setJob(data.job || null);
                    setResume(data.resume || null);
                    return;
                }
                if (publicResponse.status === 404) {
                    setError('Invalid or expired scheduling link. Please request a new one.');
                    return;
                }
                setJob(null);
                setResume(null);
                return;
            }

            // Authenticated flow: fetch full job and resume
            const jobResponse = await apiFetch(`/api/jobs/${jobId}`);
            await throwIfNotOk(jobResponse);
            const jobData = await jobResponse.json();
            setJob(jobData);

            const resumeResponse = await apiFetch(`/api/resumes/${resumeId}`);
            await throwIfNotOk(resumeResponse);
            const resumeData = await resumeResponse.json();
            setResume(resumeData);
        } catch (error) {
            setError(error.message);
        }
    };

    const fetchInterviewData = async () => {
        if (isTokenRoute || !jobId || !resumeId) return;
        try {
            const interviewsResponse = await apiFetch('/api/interviews');
            await throwIfNotOk(interviewsResponse);
            const interviewsData = await interviewsResponse.json();
            const currentInterview = interviewsData.find(inv =>
                inv.job_id === parseInt(jobId) && inv.resume_id === parseInt(resumeId)
            );
            if (currentInterview) setInterview(currentInterview);
        } catch (error) {
            console.log('No interview data found, using default duration');
        }
    };

    const fetchAvailableSlots = async () => {
        if (isTokenRoute) return; // Slots loaded from by-token response
        try {
            setIsLoading(true);
            
            if (isSelectSlotsMode) {
                // For "Select Slots" mode, fetch real available slots from backend without sending emails
                // Backend will look up interviewer_email from database if not provided
                const requestBody = {
                    resume_id: parseInt(resumeId),
                    job_id: parseInt(jobId),
                    duration_minutes: interview?.duration_minutes || 60
                };
                
                // Include interviewer_email - use from interview data or let backend handle default
                requestBody.interviewer_email = interview?.primary_interviewer || null;
                
                const response = await apiFetch('/api/fetch-available-slots', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                });

                await throwIfNotOk(response);
                const data = await response.json();
                
                // Use real available slots from backend (with busy time filtering)
                if (data.available_slots && data.available_slots.length > 0) {
                    const formattedSlots = data.available_slots.map((slot, index) => ({
                        id: index + 1,
                        start: slot.start,
                        end: slot.end,
                        formatted_time: slot.formatted_time,
                        available: true
                    }));
                    setAvailableSlots(formattedSlots);
                } else {
                    // Fallback to mock slots if no real slots available
                    const slots = generateMockSlots();
                    setAvailableSlots(slots);
                }
            } else {
                // For "Schedule Interview" mode, trigger the full workflow with email sending
                // #region agent log
                const requestBody = {
                    resume_id: parseInt(resumeId),
                    job_id: parseInt(jobId),
                    interviewer_email: interview?.primary_interviewer || null,
                    interview_title: 'Technical Interview',
                    interview_type: 'Video Call',
                    duration_minutes: 60,
                    pre_interview_notes: ''
                };
                fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ScheduleInterviewPage.js:117',message:'Calling google-schedule-interview API',data:{url:'/api/google-schedule-interview',requestBody,isSelectSlotsMode},timestamp:Date.now(),sessionId:'debug-session',runId:'schedule-interview-debug',hypothesisId:'H3'})}).catch(()=>{});
                // #endregion
                
                const response = await apiFetch('/api/google-schedule-interview', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                });

                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ScheduleInterviewPage.js:133',message:'Response received from google-schedule-interview',data:{status:response.status,statusText:response.statusText,ok:response.ok,headers:Object.fromEntries(response.headers.entries())},timestamp:Date.now(),sessionId:'debug-session',runId:'schedule-interview-debug',hypothesisId:'H1'})}).catch(()=>{});
                // #endregion

                await throwIfNotOk(response);
                const data = await response.json();
                
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ScheduleInterviewPage.js:145',message:'Successfully parsed response data',data:{hasAvailableSlots:!!data.available_slots,slotsCount:data.available_slots?.length||0,dataKeys:Object.keys(data)},timestamp:Date.now(),sessionId:'debug-session',runId:'schedule-interview-debug',hypothesisId:'H2'})}).catch(()=>{});
                // #endregion
                
                // Use real available slots from backend (with busy time filtering)
                if (data.available_slots && data.available_slots.length > 0) {
                    const formattedSlots = data.available_slots.map((slot, index) => ({
                        id: index + 1,
                        start: slot.start,
                        end: slot.end,
                        formatted_time: slot.formatted_time,
                        available: true
                    }));
                    setAvailableSlots(formattedSlots);
                } else {
                    // Fallback to mock slots if no real slots available
                    const slots = generateMockSlots();
                    setAvailableSlots(slots);
                }
            }
            
        } catch (error) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ScheduleInterviewPage.js:153',message:'Error caught in fetchAvailableSlots',data:{errorMessage:error.message,errorStack:error.stack,name:error.name},timestamp:Date.now(),sessionId:'debug-session',runId:'schedule-interview-debug',hypothesisId:'H5'})}).catch(()=>{});
            // #endregion
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const generateMockSlots = () => {
        const slots = [];
        const now = new Date();
        let slotId = 1;
        
        // Generate 2 slots per day for 8 days (16 total slots)
        // EXCLUDE weekends (Saturday=6, Sunday=0)
        for (let day = 1; day <= 8; day++) {
            const slotDate = new Date(now);
            slotDate.setDate(now.getDate() + day);
            
            // Skip weekends (Saturday=6, Sunday=0)
            if (slotDate.getDay() === 0 || slotDate.getDay() === 6) {
                continue;
            }
            
            // First slot: 10 AM
            slotDate.setHours(10, 0, 0, 0);
            slots.push({
                id: slotId++,
                start: slotDate.toISOString(),
                end: new Date(slotDate.getTime() + 60 * 60 * 1000).toISOString(),
                formatted_time: `${slotDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${slotDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
                available: true
            });
            
            // Second slot: 2 PM
            slotDate.setHours(14, 0, 0, 0);
            slots.push({
                id: slotId++,
                start: slotDate.toISOString(),
                end: new Date(slotDate.getTime() + 60 * 60 * 1000).toISOString(),
                formatted_time: `${slotDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${slotDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
                available: true
            });
        }
        
        return slots;
    };

    const handleSlotSelection = (slot) => {
        setSelectedSlot(slot);
    };

    const handleScheduleInterview = async () => {
        if (!isSelectSlotsMode && !selectedSlot) {
            alert('Please select a time slot first.');
            return;
        }
        if (isSelectSlotsMode && !selectedSlot) {
            alert('Please select a time slot first.');
            return;
        }

        try {
            setIsScheduling(true);

            if (isTokenRoute) {
                // Public submit by token (no auth)
                const url = `${getBackendUrl()}/api/public/submit-slot-by-token`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        token,
                        selected_slot: {
                            time: selectedSlot.start,
                            end: selectedSlot.end,
                            formatted: selectedSlot.formatted_time
                        }
                    })
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const msg = getUserFacingErrorMessage(
                        response.status,
                        data,
                        (typeof data.detail === 'string' && data.detail) || data.error || 'Failed to schedule interview'
                    );
                    throw new Error(msg);
                }
                alert(
                    isRecruiterScreening
                        ? 'Recruiter screening scheduled successfully! Confirmation emails have been sent.'
                        : 'Interview scheduled successfully! Confirmation emails have been sent to both parties.'
                );
                navigate('/');
                return;
            }

            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            let interviewerEmail = interview?.primary_interviewer;

            const response = await apiFetch('/api/web-schedule-interview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidate_email: resume?.email || null,
                    candidate_name: resume?.candidate_name || null,
                    interviewer_email: interviewerEmail,
                    job_title: job?.title || 'AI Solutions Developer',
                    job_id: parseInt(jobId),
                    resume_id: parseInt(resumeId),
                    interview_id: interview?.id,
                    user_timezone: userTimezone,
                    selected_slot: selectedSlot ? {
                        slot: selectedSlot.id,
                        time: selectedSlot.start,
                        end: selectedSlot.end,
                        formatted: selectedSlot.formatted_time
                    } : null
                })
            });

            await throwIfNotOk(response);
            await response.json();
            alert('Interview scheduled successfully! Confirmation emails have been sent to both parties.');
            navigate('/interviews');
        } catch (error) {
            console.error('Schedule interview error:', error);
            setError(error.message || 'Failed to schedule interview');
        } finally {
            setIsScheduling(false);
        }
    };

    if (isLoading) {
        return (
            <div className="schedule-interview-container">
                <div className="loading">
                    Loading {isRecruiterScreening ? 'screening' : 'interview'} details...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="schedule-interview-container">
                <div className="error">Error: {error}</div>
            </div>
        );
    }

    return (
        <div className="schedule-interview-container">
            <div className="glass-container interview-header">
                <h1>
                    {isSelectSlotsMode
                        ? isRecruiterScreening
                            ? 'Select Recruiter Screening Slot'
                            : 'Select Time Slots'
                        : '📅 Schedule Interview'}
                </h1>
                <div className="interview-details">
                    <h2>Job: {job?.title || 'Position'}</h2>
                    <p><strong>Candidate:</strong> {resume?.candidate_name || 'N/A'}</p>
                    <p><strong>Email:</strong> {resume?.email || 'N/A'}</p>
                    {interviewTitle ? (
                        <p><strong>Meeting:</strong> {interviewTitle}</p>
                    ) : null}
                </div>
            </div>

            <div className="glass-container slots-section">
                <h3>Select Your Preferred Time Slot</h3>
                <p>
                    {isRecruiterScreening
                        ? 'Please choose a time for your recruiter screening call:'
                        : 'Please choose from the available time slots below:'}
                </p>
                
                {isSelectSlotsMode && (
                    <div className="interview-info-box" style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        padding: '20px',
                        borderRadius: '8px',
                        margin: '20px 0',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <h4>
                            {isRecruiterScreening ? 'Recruiter Screening Details' : '📅 Interview Details'}
                        </h4>
                        <p><strong>Position:</strong> {job?.title || 'Position'}</p>
                        <p><strong>Candidate:</strong> {resume?.candidate_name || 'N/A'}</p>
                        <p><strong>Email:</strong> {resume?.email || 'N/A'}</p>
                        <p>
                            <strong>Type:</strong>{' '}
                            {isRecruiterScreening ? 'Recruiter screening (Teams)' : 'Video Call'}
                        </p>
                        <p><strong>Duration:</strong> {durationMinutes} minutes</p>
                        {!isRecruiterScreening && (
                            <p><strong>Interviewer:</strong> {interview?.primary_interviewer || 'N/A'}</p>
                        )}
                    </div>
                )}
                
                <div className="slots-grid">
                    {availableSlots.map(slot => (
                        <div
                            key={slot.id}
                            className={`slot-option ${selectedSlot?.id === slot.id ? 'selected' : ''}`}
                            onClick={() => handleSlotSelection(slot)}
                        >
                            <div className="slot-time">{slot.formatted_time}</div>
                            <div className="slot-duration">{durationMinutes} minutes</div>
                        </div>
                    ))}
                </div>

                {selectedSlot && (
                    <div className="selected-slot">
                        <h4>Selected Time Slot:</h4>
                        <p><strong>{selectedSlot.formatted_time}</strong></p>
                        <p>Duration: {durationMinutes} minutes</p>
                    </div>
                )}

                <div className="schedule-actions">
                    <button
                        className="schedule-btn"
                        onClick={handleScheduleInterview}
                        disabled={isScheduling || (!isSelectSlotsMode && !selectedSlot)}
                    >
                        {isScheduling
                            ? 'Processing...'
                            : isSelectSlotsMode
                              ? isRecruiterScreening
                                  ? 'Confirm screening slot'
                                  : 'Select Slots'
                              : 'Schedule Interview'}
                    </button>
                </div>
            </div>

            <div className="glass-container info-section">
                <h3>{isSelectSlotsMode ? 'What happens next?' : 'What happens next?'}</h3>
                <ul>
                    {isSelectSlotsMode ? (
                        <>
                            <li>Select your preferred {meetingLabel} time slot</li>
                            <li>A calendar event will be created automatically</li>
                            <li>A Teams / Meet link will be generated</li>
                            <li>Confirmation emails will be sent to both parties</li>
                        </>
                    ) : (
                        <>
                            <li>Google Calendar event will be created automatically</li>
                            <li>Google Meet link will be generated</li>
                            <li>Confirmation emails will be sent to both parties</li>
                            <li>Interview will be added to both calendars</li>
                        </>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default ScheduleInterviewPage;
